import { LitElement, html, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { personalInfoIcon, editIcon } from "@/assets/icons";
import {
  IProfileWidgetCustomization,
  USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT,
} from "@/types";
import {
  CARD_CONTAINER_CLASS,
  CARD_HEADER_CONTAINER_CLASS,
  CARD_HEADER_TITLE_CLASS,
  CARD_INPUT_LABEL_CLASS,
} from "../classes";
import { updateUser } from "@/service";
import { consume } from "@lit/context";
import { userContext, UserContextValue } from "@/context";
import { cn, showDestructiveToast } from "@/utils";

export interface PersonalInfo {
  username: string;
  first: string;
  middle: string;
  last: string;
  nickname: string;
  gender: string;
  bio: string;
}

@customElement("sb-personal-info")
export class SbPersonalInfo extends LitElement {
  @property({ type: Object }) personal!: PersonalInfo;

  @property({ type: Boolean }) showIcons: boolean = true;

  @property({ type: String }) layout: USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT =
    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW;

  @property({ type: Object })
  content: IProfileWidgetCustomization | null = null;

  @consume({ context: userContext, subscribe: true })
  private userContextValue!: UserContextValue;

  @state() private isEditing = false;
  @state() private isSaving = false;

  @state()
  private formData: PersonalInfo = this.personal;

  @state()
  private errors: Partial<Record<keyof PersonalInfo, string>> = {};

  private handleEdit() {
    if (this.isSaving) return;

    this.formData = { ...this.personal };
    this.errors = {};
    this.isEditing = true;
  }

  private handleCancel() {
    this.isEditing = false;
    this.errors = {};
  }

  private updateField<K extends keyof PersonalInfo>(key: K, value: string) {
    this.formData = {
      ...this.formData,
      [key]: value,
    };
  }

  private validate(): boolean {
    const errors: Partial<Record<keyof PersonalInfo, string>> = {};
    this.errors = errors;
    return Object.keys(errors).length === 0;
  }

  private handleSave = async () => {
    if (this.isSaving) return;

    if (!this.validate()) return;

    this.isSaving = true;

    try {
      const updatedUser = await updateUser(this.userContextValue.identifier!, {
        username: this.formData.username,
        given_name: this.formData.first,
        middle_name: this.formData.middle,
        family_name: this.formData.last,
        nickname: this.formData.nickname,
        gender: this.formData.gender,
        profile: this.formData.bio,
      });

      this.personal = {
        username: updatedUser.username ?? "",
        first: updatedUser.given_name ?? "",
        middle: updatedUser.middle_name ?? "",
        last: updatedUser.family_name ?? "",
        nickname: updatedUser.nickname ?? "",
        gender: updatedUser.gender ?? "",
        bio: updatedUser.profile ?? "",
      };

      this.isEditing = false;
    } catch (error) {
      console.error("Failed to update personal info", error);

      let errorMessage =
        "Failed to update personal information. Please try again.";

      if (error instanceof Error && "status" in error) {
        const apiError = error as any;
        errorMessage =
          apiError.data?.error?.message ?? apiError.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      showDestructiveToast(errorMessage);
    } finally {
      this.isSaving = false;
    }
  };

  createRenderRoot() {
    return this;
  }

  // Helper method to isolate either standard text input or the custom select UI
  private renderControl(key: keyof PersonalInfo) {
    if (key === "gender") {
      return html`
        <rtg-select
          .value=${this.formData.gender}
          ?disabled=${this.isSaving}
          @change=${(e: Event) =>
            this.updateField("gender", (e.target as HTMLSelectElement).value)}
        >
          <rtg-select-trigger data-testid="trigger" class="sb-w-full">
            <rtg-select-value placeholder="Select gender"></rtg-select-value>
          </rtg-select-trigger>

          <rtg-select-group slot="content">
            <rtg-select-item tabindex="0" value="Male">Male</rtg-select-item>
            <rtg-select-item tabindex="1" value="Female"
              >Female</rtg-select-item
            >
            <rtg-select-item tabindex="2" value="Other">Other</rtg-select-item>
          </rtg-select-group>
        </rtg-select>
      `;
    }

    return html`
      <rtg-input
        id=${key}
        .value=${this.formData[key]}
        ?disabled=${this.isSaving}
        @input=${(e: Event) =>
          this.updateField(key, (e.target as HTMLInputElement).value)}
      ></rtg-input>
    `;
  }
  private renderField(
    label: string,
    key: keyof PersonalInfo,
    required = false,
  ) {
    if (this.layout === USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW) {
      return html`
        <div class="sb-flex sb-w-full sb-account-settings-info-card-rows">
          <rtg-typography-p>
            <div
              class="${CARD_INPUT_LABEL_CLASS} sb-account-settings-field-label"
            >
              ${label}
            </div>
          </rtg-typography-p>
          <div class="sb-account-settings-field-input-wrapper">
            <rtg-field ?invalid=${!!this.errors[key]}>
              ${this.renderControl(key)}
              ${this.errors[key]
                ? html`<rtg-field-error>${this.errors[key]}</rtg-field-error>`
                : null}
            </rtg-field>
          </div>
        </div>
      `;
    }

    return html`
      <div
        class="sb-account-settings-field-column sb-account-settings-info-card-rows"
      >
        <div class="sb-account-settings-field-label">
          <rtg-typography-p>
            <div class=${CARD_INPUT_LABEL_CLASS}>${label}</div>
          </rtg-typography-p>
        </div>

        <div class="sb-account-settings-field-input-wrapper">
          <rtg-field ?invalid=${!!this.errors[key]}>
            ${this.renderControl(key)}
            ${this.errors[key]
              ? html`<rtg-field-error>${this.errors[key]}</rtg-field-error>`
              : null}
          </rtg-field>
        </div>
      </div>
    `;
  }

  private renderFooter(): TemplateResult {
    return html`
      <rtg-card-footer>
        <div class="sb-account-settings-card-actions">
          <rtg-button
            variant="default"
            ?disabled=${this.isSaving}
            @click=${this.handleSave}
            size="lg"
          >
            ${this.isSaving
              ? html`
                  <rtg-spinner class="rtg-icon-inline-start"></rtg-spinner>
                  Saving
                `
              : "Save"}
          </rtg-button>

          <rtg-button
            variant="ghost"
            ?disabled=${this.isSaving}
            @click=${this.handleCancel}
            size="lg"
          >
            Cancel
          </rtg-button>
        </div>
      </rtg-card-footer>
    `;
  }

  render() {
    const fields = [
      { label: "Username", key: "username" },
      { label: "First name", key: "first" },
      { label: "Middle name", key: "middle" },
      { label: "Last name", key: "last" },
      { label: "Nickname", key: "nickname" },
      { label: "Gender", key: "gender" },
      { label: "Bio", key: "bio" },
    ] as const;

    return html`
      <rtg-card editing=${this.isEditing} part="personal-info-card">
        <div class=${CARD_CONTAINER_CLASS}>
          <rtg-card-header>
            <div class=${CARD_HEADER_CONTAINER_CLASS}>
              <rtg-card-title>
                <div class=${CARD_HEADER_TITLE_CLASS}>
                  ${this.content?.icon}
                  <span>${this.content?.title ?? "Personal Info"}</span>
                </div>
              </rtg-card-title>

              ${!this.isEditing
                ? html`
                    <rtg-button
                      variant="link"
                      size="default"
                      aria-label="Edit"
                      @click=${this.handleEdit}
                    >
                      <div class="sb-flex sb-gap-1 sb-items-center">
                        ${editIcon()} Edit
                      </div>
                    </rtg-button>
                  `
                : null}
            </div>
          </rtg-card-header>

          <rtg-card-content>
            ${this.isEditing
              ? html`
                  <div
                    class=${this.layout ===
                    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                      ? "sb-grid sb-account-settings-row-layout sb-account-settings-form-body"
                      : "sb-account-settings-column-layout sb-account-settings-form-body"}
                  >
                    ${fields.map((f) => this.renderField(f.label, f.key))}
                  </div>
                `
              : html`
                  <div
                    class=${this.layout ===
                    USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                      ? "sb-grid sb-account-settings-row-layout sb-account-settings-form-body"
                      : "sb-account-settings-column-layout sb-account-settings-form-body"}
                  >
                    ${fields.map(
                      (item) => html`
                        <div
                          class=${this.layout ===
                          USER_ACCOUNT_SETTINGS_WIDGETS_LAYOUT.ROW
                            ? "sb-flex sb-w-full sb-account-settings-info-card-rows"
                            : "sb-account-settings-info-card-rows"}
                        >
                          <rtg-typography-p>
                            <div
                              class=${cn(
                                CARD_INPUT_LABEL_CLASS,
                                "sb-account-settings-field-label",
                              )}
                            >
                              ${item.label}
                            </div>
                          </rtg-typography-p>
                          <div>
                            <rtg-typography-p>
                              ${this.personal[item.key]
                                ? this.personal[item.key]
                                : html`<p class=${CARD_INPUT_LABEL_CLASS}>
                                    Not Added
                                  </p>`}
                            </rtg-typography-p>
                          </div>
                        </div>
                      `,
                    )}
                  </div>
                `}
          </rtg-card-content>

          ${this.isEditing ? html`<rtg-separator></rtg-separator>` : null}
          ${this.isEditing ? this.renderFooter() : null}
        </div>
      </rtg-card>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "sb-personal-info": SbPersonalInfo;
  }
}
